import { List, ListItemText, ListProps, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Heater } from '../types'
import { humanizeTimestamp } from '../utils/date'
import CopyableListItemButton from './CopyableListItemButton'

export interface HeaterDetailsListProps extends Omit<ListProps, 'children'> {
  heater: Heater
  showCreatedAt?: boolean
}

export default function HeaterDetailsList({
  heater,
  showCreatedAt = false,
  ...props
}: HeaterDetailsListProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('heater')

  return (
    <List {...props}>
      <CopyableListItemButton
        key="catalogProduct.model"
        copyText={heater.catalogProduct?.name || ''}
      >
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.name
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noModel')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('model')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton
        key="catalogProduct.manufacturer"
        copyText={heater.catalogProduct?.manufacturer || ''}
      >
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.manufacturer
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noManufacturer')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('manufacturer')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton
        key="catalogProduct.manufacturerProductCode"
        copyText={heater.catalogProduct?.manufacturerProductCode || ''}
      >
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.manufacturerProductCode
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noManufacturerProductCode')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('manufacturerProductCode')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton key="heatingPower" copyText={`${Math.round(heater.heatingPower)} kW`}>
        <ListItemText
          primary={`${Math.round(heater.heatingPower)} kW`}
          secondary={t('heatingPower')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton key="serialNumber" copyText={heater.serialNumber || ''}>
        <ListItemText
          primary={
            heater.serialNumber !== null && heater.serialNumber !== ''
              ? heater.serialNumber
              : t('noSerialNumber')
          }
          primaryTypographyProps={{
            color: heater.serialNumber ? 'inherit' : 'text.disabled'
          }}
          secondary={t('serialNumber')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton key="customerReference" copyText={heater.customerReference || ''}>
        <ListItemText
          primary={heater.customerReference ?? t('noCustomerReference')}
          primaryTypographyProps={{
            color: heater.customerReference ? 'inherit' : 'text.disabled'
          }}
          secondary={t('customerReference')}
        />
      </CopyableListItemButton>

      <CopyableListItemButton key="owner" copyText={heater.company?.name || ''}>
        <ListItemText
          primary={
            heater?.company ? (
              heater.company.name
            ) : heater?.companyId ? (
              <Skeleton variant="text" />
            ) : (
              t('noOwner')
            )
          }
          primaryTypographyProps={{
            color: heater.companyId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('owner')}
        />
      </CopyableListItemButton>

      {showCreatedAt && heater?.createdAt && (
        <CopyableListItemButton key="createdAt" copyText={humanizeTimestamp(heater.createdAt)}>
          <ListItemText primary={humanizeTimestamp(heater.createdAt)} secondary={t('createdAt')} />
        </CopyableListItemButton>
      )}
    </List>
  )
}
